import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle2,
  FileText,
  Quote,
  Receipt,
  Mail,
  Calendar,
  Package,
  Plus,
  Pencil,
  Trash2,
  Users,
  Copy,
  Check,
  Image as ImageIcon,
  ArrowUpRight,
} from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { DeleteConfirm } from "./DeleteConfirm";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

export interface Owner {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface PackageSub {
  id: string;
  package: { id: string; name: string };
  status: string;
  startDate: string;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  email?: string;
  phone?: string;
  kvkNumber?: string;
  vatNumber?: string;
  iban?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  website?: string;
  logoUrl?: string;
  isActive: boolean;
  // Per-bedrijf module-overrides (null = volg pakket). Geleverd door /companies.
  hasQuotesOverride?: boolean | null;
  hasExpensesOverride?: boolean | null;
  hasPaymentsOverride?: boolean | null;
  hasKasboekOverride?: boolean | null;
  createdAt: string;
  owner: Owner;
  packages: PackageSub[];
  _count: {
    contacts: number;
    invoices: number;
    quotes: number;
    expenses: number;
    members: number;
  };
}

export interface Pkg {
  id: string;
  key: string;
  name: string;
  isActive: boolean;
}

export interface UserLite {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

interface CompaniesTabProps {
  companies: Company[];
  packages: Pkg[];
  users: UserLite[];
  search: string;
  reload: () => Promise<void>;
}

type ModalState =
  | { type: "create" }
  | { type: "edit"; company: Company }
  | { type: "delete"; company: Company }
  | null;

export function CompaniesTab({ companies, packages, users, search, reload }: CompaniesTabProps) {
  const [selected, setSelected] = useState<Company | null>(null);
  const [companyDetail, setCompanyDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [assignPkg, setAssignPkg] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [ownerPick, setOwnerPick] = useState("");
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [newMemberUser, setNewMemberUser] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("USER");
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberMode, setMemberMode] = useState<"existing" | "new">("existing");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserMemberRole, setNewUserMemberRole] = useState("USER");
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createdReveal, setCreatedReveal] = useState<{ password: string; email: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Per-bedrijf module-overrides: "" = pakket-standaard, "true"/"false" = geforceerd.
  const [modQuotes, setModQuotes] = useState("");
  const [modExpenses, setModExpenses] = useState("");
  const [modPayments, setModPayments] = useState("");
  const [modKasboek, setModKasboek] = useState("");
  const [modulesLoading, setModulesLoading] = useState(false);

  const navigate = useNavigate();
  const { setCompany } = useAuth();

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  /** "Open" dit bedrijf: stel het in als actieve bedrijfscontext en ga naar
   *  het dashboard. Superadmin kan zo de boeken van elk bedrijf inzien. Het
   *  actieve pakket (incl. feature-vlaggen) wordt meegestuurd, zodat de
   *  sidebar-gating klopt voor dit pakket (bijv. Budget = alleen facturen). */
  const enterCompany = (company: Company) => {
    const pkg: any = (company.packages as any)?.[0]?.package;
    setCompany(company.id, {
      id: company.id,
      name: company.name,
      slug: company.slug,
      logoUrl: company.logoUrl,
      activePackage: pkg
        ? {
            key: pkg.key,
            name: pkg.name,
            hasQuotes: company.hasQuotesOverride ?? pkg.hasQuotes,
            hasExpenses: company.hasExpensesOverride ?? pkg.hasExpenses,
            hasPayments: company.hasPaymentsOverride ?? pkg.hasPayments,
            hasKasboek: company.hasKasboekOverride ?? pkg.hasKasboek,
            maxInvoices: pkg.maxInvoices,
            maxQuotes: pkg.maxQuotes,
            maxExpenses: pkg.maxExpenses,
            maxContacts: pkg.maxContacts,
            hasOcr: pkg.hasOcr,
            hasVatReport: pkg.hasVatReport,
          }
        : null,
    });
    navigate("/dashboard");
  };

  const filtered = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.owner.email.toLowerCase().includes(search.toLowerCase())
  );

  const openDetail = async (company: Company) => {
    setSelected(company);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/api/superadmin/companies/${company.id}`);
      setCompanyDetail(data);
      const activePkgId = data.packages?.find((p: any) => p.status === "ACTIVE")?.package?.id;
      setAssignPkg(activePkgId || "");
      setOwnerPick(data.owner?.id || company.owner.id);
      // Module-overrides initialiseren: null → "" (pakket-standaard).
      const ov = (v: any) => (v == null ? "" : v ? "true" : "false");
      setModQuotes(ov(data.hasQuotesOverride));
      setModExpenses(ov(data.hasExpensesOverride));
      setModPayments(ov(data.hasPaymentsOverride));
      setModKasboek(ov(data.hasKasboekOverride));
      setNewMemberUser("");
      setNewMemberRole("USER");
      setMemberMode("existing");
      setNewUserEmail("");
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserMemberRole("USER");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setCompanyDetail(null);
  };

  const changeOwner = async () => {
    if (!selected || !ownerPick) return;
    setOwnerLoading(true);
    try {
      await api.patch(`/api/superadmin/companies/${selected.id}/owner`, { ownerId: ownerPick });
      await reload();
      await openDetail(selected);
      flash("Eigenaar gewijzigd.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Eigenaar wijzigen mislukt");
    } finally {
      setOwnerLoading(false);
    }
  };

  const addMember = async () => {
    if (!selected || !newMemberUser) return;
    setMemberLoading(true);
    try {
      await api.post(`/api/superadmin/companies/${selected.id}/members`, {
        userId: newMemberUser,
        role: newMemberRole,
      });
      await reload();
      await openDetail(selected);
      setNewMemberUser("");
      flash("Lid toegevoegd.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Lid toevoegen mislukt");
    } finally {
      setMemberLoading(false);
    }
  };

  const removeMember = async (userId: string) => {
    if (!selected) return;
    try {
      await api.delete(`/api/superadmin/companies/${selected.id}/members/${userId}`);
      await reload();
      await openDetail(selected);
      flash("Lid verwijderd.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Lid verwijderen mislukt");
    }
  };

  const createUserForCompany = async () => {
    if (!selected || !newUserEmail.trim()) return;
    setCreateUserLoading(true);
    try {
      const { data } = await api.post("/api/superadmin/users", {
        email: newUserEmail,
        firstName: newUserFirstName,
        lastName: newUserLastName,
        role: "USER",
        companyId: selected.id,
        memberRole: newUserMemberRole,
      });
      await reload();
      await openDetail(selected);
      setNewUserEmail("");
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserMemberRole("USER");
      if (data.generatedPassword) {
        setCreatedReveal({ password: data.generatedPassword, email: newUserEmail });
      } else {
        flash("Gebruiker aangemaakt en gekoppeld.");
      }
    } catch (err: any) {
      flash(err?.response?.data?.error || "Gebruiker aanmaken mislukt");
    } finally {
      setCreateUserLoading(false);
    }
  };

  const toggleStatus = async (company: Company) => {
    try {
      await api.patch(`/api/superadmin/companies/${company.id}/status`, {
        isActive: !company.isActive,
      });
      await reload();
      if (selected?.id === company.id) {
        setSelected((s) => (s ? { ...s, isActive: !s.isActive } : s));
      }
      flash(`Bedrijf ${company.isActive ? "gepauzeerd" : "geactiveerd"}.`);
    } catch (err: any) {
      flash(err?.response?.data?.error || "Actie mislukt");
    }
  };

  const assignPackage = async () => {
    if (!selected || !assignPkg) return;
    setAssignLoading(true);
    try {
      await api.post(`/api/superadmin/companies/${selected.id}/package`, { packageId: assignPkg });
      await reload();
      await openDetail(selected);
      flash("Pakket gewijzigd.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Pakket wijzigen mislukt");
    } finally {
      setAssignLoading(false);
    }
  };

  const saveModules = async () => {
    if (!selected) return;
    setModulesLoading(true);
    try {
      const toVal = (v: string) => (v === "" ? null : v === "true");
      await api.patch(`/api/superadmin/companies/${selected.id}/modules`, {
        hasQuotesOverride: toVal(modQuotes),
        hasExpensesOverride: toVal(modExpenses),
        hasPaymentsOverride: toVal(modPayments),
        hasKasboekOverride: toVal(modKasboek),
      });
      await reload();
      await openDetail(selected);
      flash("Modules voor dit bedrijf opgeslagen.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Modules opslaan mislukt");
    } finally {
      setModulesLoading(false);
    }
  };

  const activePackages = packages.filter((p) => p.isActive);

  // Pakket-limiet voor het geselecteerde bedrijf (uit detail-data, dus altijd actueel).
  const detailActivePkg = companyDetail?.packages?.find((p: any) => p.status === "ACTIVE")?.package;
  const detailMaxUsers: number | null = detailActivePkg?.maxUsers ?? null;
  const detailMemberCount = companyDetail?.members?.length ?? 0;
  const detailAtLimit = detailMaxUsers != null && detailMemberCount >= detailMaxUsers;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {toast && (
        <div className="lg:col-span-3 mb-2 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2.5 text-sm font-semibold">
          <CheckCircle2 size={24} /> {toast}
        </div>
      )}

      <div className="lg:col-span-2 space-y-4">
        {filtered.map((company) => (
          <motion.div
            key={company.id}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`p-5 rounded-2xl border transition-all ${
              selected?.id === company.id
                ? "border-brand-500 bg-gradient-to-r from-brand-50/60 to-white"
                : "border-white/60 bg-white/75 backdrop-blur hover:shadow-md"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div
                className="flex items-center gap-4 cursor-pointer flex-1 min-w-0"
                onClick={() => openDetail(company)}
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-indigo-500 text-white flex items-center justify-center font-bold text-lg shadow-lg shadow-brand-500/20 shrink-0">
                  {company.name[0]}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-800 truncate">{company.name}</h3>
                  <p className="text-sm text-slate-500 truncate">{company.owner.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => enterCompany(company)}
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2.5 py-1.5 text-xs font-bold text-brand-700 hover:bg-brand-100 transition-colors"
                  title="Openen (als superadmin in dit bedrijf werken)"
                >
                  Openen
                  <ArrowUpRight size={15} />
                </button>
                <button
                  onClick={() => setModal({ type: "edit", company })}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600 transition-colors"
                  title="Bewerken"
                >
                  <Pencil size={24} />
                </button>
                <button
                  onClick={() => toggleStatus(company)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-colors border ${
                    company.isActive
                      ? "bg-emerald-100/80 text-emerald-700 border-emerald-200 hover:bg-emerald-200/80"
                      : "bg-amber-100/80 text-amber-700 border-amber-200 hover:bg-amber-200/80"
                  }`}
                  title={company.isActive ? "Pauzeren" : "Activeren"}
                >
                  {company.isActive ? "Actief" : "Gepauzeerd"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
              {[
                { icon: FileText, count: company._count.invoices, label: "Facturen" },
                { icon: Quote, count: company._count.quotes, label: "Offertes" },
                { icon: Receipt, count: company._count.expenses, label: "Uitgaven" },
                { icon: Users, count: company._count.members, label: "Gebruikers" },
              ].map((item) => (
                <button
                  key={item.label}
                  className="text-center hover:bg-slate-50 rounded-lg py-1 transition-colors"
                  onClick={() => openDetail(company)}
                >
                  <item.icon size={24} className="mx-auto mb-1 text-brand-400" />
                  <p className="font-bold text-slate-800">{item.count}</p>
                  <p className="text-slate-500 text-xs">{item.label}</p>
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <Package size={21} />
                <span>{company.packages[0]?.package.name || "Geen pakket"}</span>
                <span className="mx-1">•</span>
                <Calendar size={21} />
                <span>{new Date(company.createdAt).toLocaleDateString("nl-NL")}</span>
              </div>
              <button
                onClick={() => setModal({ type: "delete", company })}
                className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                title="Verwijderen"
              >
                <Trash2 size={22} />
              </button>
            </div>
          </motion.div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center text-slate-400 py-12">Geen bedrijven gevonden.</div>
        )}
      </div>

      <div>
        <AnimatePresence mode="wait">
          {selected && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card className="p-5 sticky top-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="font-bold text-lg">{selected.name}</h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => enterCompany(selected)}
                      className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2.5 py-1.5 text-xs font-bold text-brand-700 hover:bg-brand-100 transition-colors"
                      title="Openen (als superadmin in dit bedrijf werken)"
                    >
                      Openen
                      <ArrowUpRight size={15} />
                    </button>
                    <button
                      onClick={closeDetail}
                      className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X size={27} />
                    </button>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail size={24} /> {selected.owner.email}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar size={24} /> Aangemaakt:{" "}
                    {new Date(selected.createdAt).toLocaleDateString("nl-NL")}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Package size={24} /> Pakket:{" "}
                    {selected.packages[0]?.package.name || "Geen"}
                  </div>
                </div>

                {/* Abonnement-sectie */}
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-2">Abonnement</p>
                  <Select
                    value={assignPkg}
                    onChange={(e) => setAssignPkg(e.target.value)}
                    options={[
                      { value: "", label: "— Selecteer pakket —" },
                      ...activePackages.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full mt-2"
                    onClick={assignPackage}
                    loading={assignLoading}
                    disabled={!assignPkg}
                  >
                    Pakket wijzigen
                  </Button>
                </div>

                {/* Modules (per bedrijf) — override op pakket-niveau */}
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                    Modules (per bedrijf)
                  </p>
                  <p className="text-[0.6875rem] text-slate-400 mb-2">
                    Standaard volgt dit bedrijf het pakket. Forceer per pagina aan/uit voor
                    alleen dit bedrijf (overschrijft het pakket).
                  </p>
                  <div className="space-y-2">
                    {[
                      {
                        key: "hasQuotes",
                        label: "Offertes",
                        state: modQuotes,
                        set: setModQuotes,
                        def: detailActivePkg?.hasQuotes,
                      },
                      {
                        key: "hasExpenses",
                        label: "Uitgaven",
                        state: modExpenses,
                        set: setModExpenses,
                        def: detailActivePkg?.hasExpenses,
                      },
                      {
                        key: "hasPayments",
                        label: "Inkomsten",
                        state: modPayments,
                        set: setModPayments,
                        def: detailActivePkg?.hasPayments,
                      },
                      {
                        key: "hasKasboek",
                        label: "Kasboek",
                        state: modKasboek,
                        set: setModKasboek,
                        def: detailActivePkg?.hasKasboek,
                      },
                    ].map((m) => (
                      <div
                        key={m.key}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="font-semibold text-slate-700">{m.label}</span>
                        <select
                          value={m.state}
                          onChange={(e) => m.set(e.target.value)}
                          className="px-2 py-1.5 rounded-xl border border-slate-200 bg-white/80 text-sm"
                        >
                          <option
                            value=""
                          >{`Standaard (pakket: ${
                            m.def == null ? "—" : m.def ? "aan" : "uit"
                          })`}</option>
                          <option value="true">Aan (geforceerd)</option>
                          <option value="false">Uit (geforceerd)</option>
                        </select>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full mt-2"
                    onClick={saveModules}
                    loading={modulesLoading}
                  >
                    Modules opslaan
                  </Button>
                </div>

                <div className="mt-5 flex gap-2">
                  <Button
                    onClick={() => toggleStatus(selected)}
                    variant={selected.isActive ? "outline" : "primary"}
                    className="flex-1"
                  >
                    {selected.isActive ? "Pauzeren" : "Activeren"}
                  </Button>
                  <Button
                    onClick={() => setModal({ type: "edit", company: selected })}
                    variant="outline"
                  >
                    <Pencil size={24} />
                  </Button>
                </div>

                {detailLoading ? (
                  <div className="mt-5 pt-5 border-t border-slate-100 text-center text-sm text-slate-400">
                    Laden...
                  </div>
                ) : (
                  companyDetail && (
                    <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
                      {/* Eigenaar wijzigen */}
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-2">Eigenaar</p>
                        <div className="flex gap-2">
                          <select
                            value={ownerPick}
                            onChange={(e) => setOwnerPick(e.target.value)}
                            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"
                          >
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.email}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={changeOwner}
                            loading={ownerLoading}
                            disabled={ownerPick === (companyDetail.owner?.id || selected.owner.id)}
                          >
                            Wijzig
                          </Button>
                        </div>
                      </div>

                      {/* Leden */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold text-slate-400 uppercase">
                            Leden ({detailMemberCount}
                            {detailMaxUsers == null ? "" : ` / ${detailMaxUsers}`})
                          </p>
                          <span className="text-[0.6875rem] text-slate-400">
                            {detailMaxUsers == null
                              ? "onbeperkt"
                              : detailAtLimit
                                ? "limiet bereikt"
                                : `${detailMaxUsers - detailMemberCount} plek(k) over`}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {companyDetail.members?.map((m: any) => {
                            const isOwner = m.user.id === (companyDetail.owner?.id || selected.owner.id);
                            return (
                              <div
                                key={m.id}
                                className="flex items-center justify-between text-sm gap-2"
                              >
                                <span className="truncate">
                                  {m.user.firstName || m.user.lastName
                                    ? `${m.user.firstName || ""} ${m.user.lastName || ""}`.trim()
                                    : m.user.email}
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge>{m.role}</Badge>
                                  {!isOwner && (
                                    <button
                                      onClick={() => removeMember(m.user.id)}
                                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                                      title="Lid verwijderen"
                                    >
                                      <Trash2 size={21} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {(!companyDetail.members || companyDetail.members.length === 0) && (
                            <p className="text-xs text-slate-400">Nog geen leden.</p>
                          )}
                        </div>

                        {detailAtLimit && (
                          <p className="mt-2 text-[0.6875rem] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                            Pakket-limiet bereikt ({detailMaxUsers} gebruikers). Upgrade het pakket
                            of verwijder eerst een lid.
                          </p>
                        )}

                        {/* Toggle: bestaand / nieuw */}
                        <div className="mt-3 flex gap-1.5 bg-slate-100 rounded-xl p-1">
                          {(["existing", "new"] as const).map((m) => (
                            <button
                              key={m}
                              onClick={() => setMemberMode(m)}
                              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                memberMode === m
                                  ? "bg-white shadow-sm text-brand-600"
                                  : "text-slate-500 hover:text-slate-700"
                              }`}
                            >
                              {m === "existing" ? "Bestaand koppelen" : "Nieuwe aanmaken"}
                            </button>
                          ))}
                        </div>

                        {memberMode === "existing" ? (
                          <div className="mt-2 flex flex-col gap-2">
                            <div className="flex gap-2">
                              <select
                                value={newMemberUser}
                                onChange={(e) => setNewMemberUser(e.target.value)}
                                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"
                              >
                                <option value="">— Selecteer gebruiker —</option>
                                {users
                                  .filter(
                                    (u) =>
                                      !companyDetail.members?.some((m: any) => m.user.id === u.id)
                                  )
                                  .map((u) => (
                                    <option key={u.id} value={u.id}>
                                      {u.email}
                                    </option>
                                  ))}
                              </select>
                              <select
                                value={newMemberRole}
                                onChange={(e) => setNewMemberRole(e.target.value)}
                                className="px-2 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"
                              >
                                <option value="USER">Gebruiker</option>
                                <option value="ADMIN">Beheerder</option>
                              </select>
                            </div>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={addMember}
                              loading={memberLoading}
                              disabled={!newMemberUser || detailAtLimit}
                            >
                              <Plus size={21} /> Lid toevoegen
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-2 flex flex-col gap-2">
                            <Input
                              label="E-mail"
                              type="email"
                              value={newUserEmail}
                              onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                label="Voornaam"
                                value={newUserFirstName}
                                onChange={(e) => setNewUserFirstName(e.target.value)}
                              />
                              <Input
                                label="Achternaam"
                                value={newUserLastName}
                                onChange={(e) => setNewUserLastName(e.target.value)}
                              />
                            </div>
                            <select
                              value={newUserMemberRole}
                              onChange={(e) => setNewUserMemberRole(e.target.value)}
                              className="px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"
                            >
                              <option value="USER">Rol: Gebruiker</option>
                              <option value="ADMIN">Rol: Beheerder</option>
                            </select>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={createUserForCompany}
                              loading={createUserLoading}
                              disabled={!newUserEmail.trim() || detailAtLimit}
                            >
                              <Plus size={21} /> Gebruiker aanmaken & koppelen
                            </Button>
                            <p className="text-[0.6875rem] text-slate-400">
                              Er wordt een account gemaakt (globale rol: gebruiker) en direct als
                              lid aan dit bedrijf gekoppeld. Een tijdelijk wachtwoord wordt getoond.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      {modal?.type === "create" && (
        <CompanyFormModal
          packages={activePackages}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "edit" && (
        <CompanyFormModal
          company={modal.company}
          packages={activePackages}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            if (selected?.id === modal.company.id) await openDetail(modal.company).catch(() => {});
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteConfirm
          isOpen
          onClose={() => setModal(null)}
          entityName={modal.company.name}
          label="bedrijfsnaam"
          title="Bedrijf verwijderen"
          onConfirm={async () => {
            await api.delete(`/api/superadmin/companies/${modal.company.id}`, {
              data: { confirmName: modal.company.name },
            });
            await reload();
            closeDetail();
            flash("Bedrijf verwijderd.");
          }}
        >
          <span>
            Bedrijf <strong>{modal.company.name}</strong> inclusief alle facturen, offertes,
            contacten en abonnementen wordt permanent verwijderd.
          </span>
        </DeleteConfirm>
      )}

      {/* Wachtwoord-reveal na aanmaken gebruiker voor dit bedrijf */}
      {createdReveal && (
        <PasswordReveal
          password={createdReveal.password}
          email={createdReveal.email}
          onClose={() => {
            setCreatedReveal(null);
            flash("Gebruiker aangemaakt en gekoppeld.");
          }}
        />
      )}

      {/* Floating create button */}
      <button
        onClick={() => setModal({ type: "create" })}
        className="fixed bottom-8 right-8 w-14 h-14 rounded-2xl bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/30 flex items-center justify-center hover:scale-105 transition-transform z-30"
        title="Nieuw bedrijf"
      >
        <Plus size={39} />
      </button>
    </div>
  );
}

interface CompanyFormModalProps {
  company?: Company;
  packages: Pkg[];
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
}

function CompanyFormModal({ company, packages, onClose, onSaved }: CompanyFormModalProps) {
  const isEdit = !!company;
  const [name, setName] = useState(company?.name || "");
  const [kvkNumber, setKvk] = useState(company?.kvkNumber || "");
  const [vatNumber, setVat] = useState(company?.vatNumber || "");
  const [iban, setIban] = useState(company?.iban || "");
  const [address, setAddress] = useState(company?.address || "");
  const [zipCode, setZipCode] = useState(company?.zipCode || "");
  const [city, setCity] = useState(company?.city || "");
  const [country, setCountry] = useState(company?.country || "Nederland");
  const [phone, setPhone] = useState(company?.phone || "");
  const [email, setEmail] = useState(company?.email || "");
  const [website, setWebsite] = useState(company?.website || "");
  const [logoUrl, setLogoUrl] = useState<string | undefined>(company?.logoUrl);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  // create-only
  const [ownerMode, setOwnerMode] = useState<"new" | "existing">("new");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [packageId, setPackageId] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3300";

  const uploadLogo = async (companyId: string, file: File) => {
    const fd = new FormData();
    fd.append("logo", file);
    setLogoUploading(true);
    try {
      const { data } = await api.post(`/api/superadmin/companies/${companyId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.logoUrl);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Logo uploaden mislukt");
    } finally {
      setLogoUploading(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!name.trim()) {
      setError("Bedrijfsnaam is verplicht");
      return;
    }
    if (!isEdit && ownerMode === "new" && !ownerEmail.trim()) {
      setError("Eigenaar-e-mail is verplicht");
      return;
    }
    setLoading(true);
    try {
      const profile = {
        name,
        kvkNumber,
        vatNumber,
        iban,
        address,
        zipCode,
        city,
        country,
        phone,
        email,
        website,
      };
      if (isEdit) {
        await api.patch(`/api/superadmin/companies/${company!.id}`, profile);
        if (logoFile) await uploadLogo(company!.id, logoFile);
        await onSaved("Bedrijf opgeslagen.");
      } else {
        const body: any = {
          ...profile,
          ...(ownerMode === "new"
            ? { ownerEmail, ownerFirstName, ownerLastName }
            : {}),
          ...(packageId ? { packageId } : {}),
        };
        const { data } = await api.post("/api/superadmin/companies", body);
        // Logo pas na aanmaken uploaden (we hebben dan een company-id).
        if (logoFile && data?.id) {
          try {
            await uploadLogo(data.id, logoFile);
          } catch (e: any) {
            // bedrijf is aangemaakt; logo-fout niet fataal maar tonen.
            setError(e.message || "Logo uploaden mislukt");
          }
        }
        if (data.generatedPassword) {
          setGeneratedPassword(data.generatedPassword);
          return; // keep modal open to show password
        }
        await onSaved("Bedrijf aangemaakt.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  if (generatedPassword) {
    return (
      <Modal
        isOpen
        onClose={() => onSaved("Bedrijf aangemaakt.")}
        title="Bedrijf aangemaakt"
        size="sm"
        footer={
          <Button
            variant="primary"
            className="w-full"
            onClick={() => onSaved("Bedrijf aangemaakt.")}
          >
            Gereed
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Het bedrijf is aangemaakt. Deel dit tijdelijke wachtwoord eenmalig met de eigenaar:
          </p>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 font-mono text-center text-lg font-bold text-slate-800 break-all">
            {generatedPassword}
          </div>
          <p className="text-xs text-slate-400">
            Het wachtwoord is alleen nu zichtbaar; bewaar het veilig.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Bedrijf bewerken" : "Nieuw bedrijf"}
      subtitle={isEdit ? company!.name : "Maak een bedrijf aan met eigenaar en pakket"}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={submit} loading={loading}>
              {isEdit ? "Opslaan" : "Aanmaken"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Input label="Bedrijfsnaam" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        {/* Logo */}
        <div className="md:col-span-2">
          <p className="text-sm font-semibold text-slate-700 mb-1.5">Logo</p>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? (
                <img
                  src={logoUrl.startsWith("http") ? logoUrl : `${API_URL}${logoUrl}`}
                  alt="logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <ImageIcon size={30} className="text-slate-300" />
              )}
            </div>
            <div className="flex-1 space-y-1.5">
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <ImageIcon size={18} />
                {logoUrl ? "Logo vervangen" : "Logo kiezen"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setLogoFile(f);
                  }}
                />
              </label>
              {logoFile && (
                <p className="text-xs text-slate-500 truncate">{logoFile.name} — opgeslagen bij aanmaken/bewerken.</p>
              )}
              {logoUploading && <p className="text-xs text-brand-600">Uploaden…</p>}
            </div>
          </div>
        </div>

        <Input label="KVK-nummer" value={kvkNumber} onChange={(e) => setKvk(e.target.value)} />
        <Input label="BTW-nummer" value={vatNumber} onChange={(e) => setVat(e.target.value)} />
        <Input label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />
        <Input label="Telefoon" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <div className="md:col-span-2">
          <Input label="Adres (straat + huisnummer)" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <Input label="Postcode" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
        <Input label="Plaats" value={city} onChange={(e) => setCity(e.target.value)} />
        <Input label="Land" value={country} onChange={(e) => setCountry(e.target.value)} />
        <Input label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
        <div className="md:col-span-2">
          <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        {!isEdit && (
          <>
            <div className="md:col-span-2 pt-2 border-t border-slate-100">
              <p className="text-sm font-bold text-slate-700 mb-2">Eigenaar</p>
              <div className="flex gap-2 mb-3">
                {(["new", "existing"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setOwnerMode(m)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      ownerMode === m
                        ? "bg-brand-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {m === "new" ? "Nieuwe eigenaar" : "Bestaande gebruiker"}
                  </button>
                ))}
              </div>
            </div>
            {ownerMode === "new" && (
              <>
                <Input
                  label="Eigenaar e-mail"
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                />
                <Input
                  label="Voornaam (optioneel)"
                  value={ownerFirstName}
                  onChange={(e) => setOwnerFirstName(e.target.value)}
                />
                <Input
                  label="Achternaam (optioneel)"
                  value={ownerLastName}
                  onChange={(e) => setOwnerLastName(e.target.value)}
                />
              </>
            )}
            {ownerMode === "existing" && (
              <div className="md:col-span-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                Bestaande eigenaar-koppeling gebeurt via de gebruikers-tab. Kies hieronder een
                startpakket; de eigenaar kan later via Gebruikers worden toegevoegd aan dit bedrijf.
              </div>
            )}
            <div className="md:col-span-2">
              <Select
                label="Startpakket"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                options={[
                  { value: "", label: "— Standaard (budget-factuur) —" },
                  ...packages.map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function PasswordReveal({
  password,
  email,
  onClose,
}: {
  password: string;
  email: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Gebruiker aangemaakt"
      size="sm"
      footer={
        <Button variant="primary" className="w-full" onClick={onClose}>
          Gereed
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Tijdelijk wachtwoord voor <strong>{email}</strong>:
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200 font-mono text-lg font-bold text-slate-800 break-all">
            {password}
          </div>
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check size={24} /> : <Copy size={24} />}
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          Dit wachtwoord is alleen nu zichtbaar. Deel het veilig met de gebruiker.
        </p>
      </div>
    </Modal>
  );
}